import {
  LoadingProgressProvider,
  useOptionalLoadingProgress,
} from "@/lib/loading";
import { ReactNode, useEffect, useRef } from "react";

/**
 * Composant interne qui affiche l'interface de chargement
 * Utilise le contexte de progression si disponible
 * 
 * ⚠️ Doit être utilisé dans un LoadingProgressProvider (ex: WithLoadingProgress)
 */
export function LoadingUI() {
  const loadingContext = useOptionalLoadingProgress();
  const warnedRef = useRef(false);

  // Valeurs par défaut si pas de contexte ou pas d'étapes
  const progress = loadingContext?.progress ?? 0;
  const currentLabel = loadingContext?.currentLabel ?? "Chargement...";
  const hasSteps = loadingContext && loadingContext.steps.length > 0;
  const hasError = loadingContext?.hasError ?? false;
  const errorMessage = loadingContext?.errorMessage;
  const isComplete = loadingContext?.isComplete ?? false;

  // Avertissement en dev si aucune étape n'est définie après un délai
  useEffect(() => {
    if (import.meta.env.DEV && loadingContext && !warnedRef.current) {
      const timer = setTimeout(() => {
        if (!loadingContext.steps.length && !warnedRef.current) {
          warnedRef.current = true;
          console.warn(
            `%c[LoadingProgress] ⚠️ Aucune étape de chargement définie!

📖 RAPPEL POUR LES DÉVELOPPEURS:
   La page en cours de chargement n'a pas défini ses étapes de progression.
   Un spinner simple est affiché à la place de la barre de progression.

   Pour ajouter la progression à votre page:

   1. Créer un fichier de constantes (ex: MaPage.constants.ts):
      export const MA_PAGE_STEPS: LoadingStep[] = [
        { id: 'init', label: 'Initialisation...', weight: 30 },
        { id: 'data', label: 'Chargement...', weight: 70 },
      ];

   2. Dans votre composant, utiliser le hook:
      const { completeStep } = useLoadingProgress(MA_PAGE_STEPS);

   3. Reporter la progression:
      completeStep('init');
      // après chargement...
      completeStep('data');

📁 Exemple: src/pages/Viewer.tsx et src/pages/Viewer.constants.ts
`,
            "color: orange",
          );
        }
      }, 2000); // Attendre 2s avant d'afficher l'avertissement

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [loadingContext]);

  // Ne rien afficher si le chargement est terminé
  if (isComplete) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50">
      <div className="relative h-full w-full bg-white overflow-hidden flex flex-col items-center justify-center">
        {/* Fond avec halo blanc en fondu pour améliorer la lisibilité du logo */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at center, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 35%, rgba(0,0,0,0) 70%)",
          }}
        />

        <div className="relative z-10 flex flex-col items-center justify-center">
          {/* Logo / Titre */}
          <div className="mb-8">
            <img
              src="/assets/PIXAtwin.png"
              alt="Logo PIXAtwin"
              className="h-28 w-auto"
            />
          </div>

      {/* Barre de progression ou Spinner */}
      {hasSteps ? (
        <div className="w-64 space-y-3">
          {/* Barre de progression */}
          <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ease-out rounded-full ${
                hasError ? "bg-red-500" : "bg-blue-500"
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Pourcentage */}
          <div className="flex justify-between text-xs text-gray-500">
            <span>{progress}%</span>
            {hasError && <span className="text-red-400">Erreur</span>}
          </div>
        </div>
      ) : (
        /* Spinner pour les pages sans étapes définies */
        <div className="relative">
          <div className="w-12 h-12 border-4 border-gray-700 border-t-blue-500 rounded-full animate-spin" />
        </div>
      )}

      {/* Texte de chargement / Erreur */}
      {hasError ? (
        <div className="mt-6 text-center">
          <p className="text-red-400 text-sm">{errorMessage}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded transition-colors"
          >
            Réessayer
          </button>
        </div>
      ) : (
        <p className="mt-6 text-gray-400 text-sm animate-pulse">
          {currentLabel}
        </p>
      )}

        </div>
      </div>
    </div>
  );
}

interface PageLoaderProps {
  children?: ReactNode;
}

/**
 * Composant de chargement avec système de progression
 *
 * Ce composant sert de fallback pour React.lazy() et fournit
 * le contexte de progression aux pages enfants.
 *
 * ═══════════════════════════════════════════════════════════════
 * 📖 UTILISATION
 * ═══════════════════════════════════════════════════════════════
 *
 * 1. Dans App.tsx, utiliser comme fallback de Suspense:
 *
 *    ```tsx
 *    <Suspense fallback={<PageLoader />}>
 *      <Viewer />
 *    </Suspense>
 *    ```
 *
 * 2. Dans la page enfant, utiliser useLoadingProgress:
 *
 *    ```tsx
 *    const { completeStep } = useLoadingProgress(VIEWER_STEPS);
 *    ```
 *
 * ═══════════════════════════════════════════════════════════════
 * 🔧 COMPORTEMENT
 * ═══════════════════════════════════════════════════════════════
 *
 * - Sans étapes définies: Affiche un spinner simple
 * - Avec étapes: Affiche une barre de progression
 * - En cas d'erreur: Affiche le message + bouton réessayer
 *
 * @see useLoadingProgress - Hook pour reporter la progression
 * @see LoadingStep - Type pour définir les étapes
 */
export function PageLoader({ children }: PageLoaderProps) {
  // Si des children sont passés, on wrappe avec le provider
  // Sinon, on affiche juste l'UI de chargement
  if (children) {
    return (
      <LoadingProgressProvider>
        <LoadingUI />
        {children}
      </LoadingProgressProvider>
    );
  }

  // Mode fallback Suspense: juste l'UI
  return (
    <LoadingProgressProvider>
      <LoadingUI />
    </LoadingProgressProvider>
  );
}

/**
 * Wrapper qui fournit le contexte de progression à une page
 * Utilisé pour envelopper les pages qui ont besoin de reporter leur progression
 *
 * @example
 * ```tsx
 * <WithLoadingProgress>
 *   <Viewer />
 * </WithLoadingProgress>
 * ```
 */
export function WithLoadingProgress({ children }: { children: ReactNode }) {
  return <LoadingProgressProvider>{children}</LoadingProgressProvider>;
}
